import { shortDateTime } from '../../shared/dateTime';
import type { DepartureEstimate } from './unassignDeparture';
import type { UnassignRequest } from './orderActionsApi';

/**
 * Przekazanie obsadzonego zlecenia do Trade i wniosek o zmianę pojazdu po
 * wyjeździe auta na załadunek (makieta back office v2, fala B, B6;
 * 21.10.2026; BR-FTL-05, ścieżki FTL-01 i MIX-04) — front tylko pokazuje to,
 * co policzyło API (`tradeTransferBeforeDeparture`, `vehicleChangeRequestable`,
 * `departureEstimate` na karcie zlecenia). Reguły wyjazdu i zakresu tu nie
 * ma; rozstrzyga zawsze API.
 */

/** Klucze przełączników — jak w API (`feature-flags.ts`), nie przemianowujemy. */
export const FEATURE_TRADE_TRANSFER_BEFORE_DEPARTURE = 'tradeTransferBeforeDeparture';
export const FEATURE_VEHICLE_CHANGE_REQUEST = 'vehicleChangeRequest';

/** Rodzaje wniosku — dane z API (`UnassignRequest.kind`). */
export const REQUEST_KIND = {
  UNASSIGN: 'unassign',
  TRADE_TRANSFER: 'tradeTransfer',
  VEHICLE_CHANGE: 'vehicleChange',
} as const;

/** Rodzaj wniosku z karty; brak — odpięcie (wnioski sprzed 21.10.2026). */
export function requestKindOf(request: Pick<UnassignRequest, 'kind'>): string {
  return request.kind ?? REQUEST_KIND.UNASSIGN;
}

/** Co planista robi z przekazaniem obsadzonego zlecenia: sam (przed wyjazdem) albo wnioskiem. */
export interface TradeTransferAccess {
  self: boolean;
  request: boolean;
}

/** Przekazanie do Trade z karty; `null`, gdy API pola nie podało (przełącznik, rola, spółka). */
export function tradeTransferAccessOf(
  record: { [field: string]: unknown },
  featureOn: boolean,
): TradeTransferAccess | null {
  if (!featureOn) return null;
  const raw = record.tradeTransferBeforeDeparture as Partial<TradeTransferAccess> | null | undefined;
  if (!raw || typeof raw !== 'object') return null;
  return { self: raw.self === true, request: raw.request === true };
}

/** Czy pytający składa wniosek o zmianę pojazdu — tylko gdy API mówi wprost `true`. */
export function vehicleChangeRequestableOf(
  record: { [field: string]: unknown },
  featureOn: boolean,
): boolean {
  return featureOn && record.vehicleChangeRequestable === true;
}

/** „ (szac. 21.10, 06:15)” — przy szacunku; przy fakcie bez godziny pusto. */
function estimateSuffix(e: DepartureEstimate | null): string {
  return e?.at ? ` (szac. ${shortDateTime(e.at)})` : '';
}

/** Przed wyjazdem — podpis przy „Przekaż do Trade”. */
export function selfTradeTransferText(e: DepartureEstimate | null): string {
  return `Do wyjazdu na załadunek${estimateSuffix(e)} przekazujesz obsadzone zlecenie do Trade sam, bez wniosku`;
}

/** Pierwsze zdanie o wyjeździe — fakt, szacunek albo założenie zachowawcze (jak przy odpięciu). */
function departedLead(e: DepartureEstimate | null): string {
  if (e?.basis === 'noLoadingDate') return 'Nie wiadomo, kiedy auto wyrusza na załadunek';
  if (e?.basis === 'loadingDayStart') {
    return `Zachowawczo przyjmujemy, że auto wyruszyło na załadunek${estimateSuffix(e)}`;
  }
  return `Auto wyruszyło na załadunek${estimateSuffix(e)}`;
}

/** Po wyjeździe — informacja przy wniosku o przekazanie. */
export function departedTradeTransferText(e: DepartureEstimate | null): string {
  return `${departedLead(e)} — przekazanie do Trade zatwierdza przełożony`;
}

/** Po wyjeździe — informacja przy wniosku o zmianę pojazdu. */
export function departedVehicleChangeText(e: DepartureEstimate | null): string {
  return e
    ? `${departedLead(e)} — zmianę pojazdu zatwierdza Team Leader`
    : 'Zmianę pojazdu zatwierdza Team Leader';
}

/** Nagłówek banera wniosku — po rodzaju. */
export function requestAlertTitle(kind: string): string {
  if (kind === REQUEST_KIND.TRADE_TRANSFER) return 'Wniosek o przekazanie do Trade czeka na decyzję';
  if (kind === REQUEST_KIND.VEHICLE_CHANGE) return 'Wniosek o zmianę pojazdu czeka na decyzję';
  return 'Wniosek o odpięcie czeka na decyzję';
}

/** Wyraźna linia banera wniosków z 21.10.2026: kto prosi i o co. */
export function changeRequestText(request: UnassignRequest): string {
  const vehicle = request.registrationNumber ? ` ${request.registrationNumber}` : '';
  if (requestKindOf(request) === REQUEST_KIND.VEHICLE_CHANGE) {
    const proposed = request.proposedRegistrationNumber
      ? ` na ${request.proposedRegistrationNumber}`
      : request.proposedCarId != null
        ? ` na #${request.proposedCarId}`
        : '';
    if (request.mine) {
      return `Twój wniosek o zmianę pojazdu${vehicle}${proposed} czeka na decyzję Team Leadera.`;
    }
    const who = request.requestedBy?.username ?? 'Planista';
    return `${who} prosi o zmianę pojazdu${vehicle}${proposed}.`;
  }
  if (request.mine) {
    return `Twój wniosek o przekazanie do Trade zlecenia obsadzonego pojazdem${vehicle} czeka na decyzję przełożonego.`;
  }
  const who = request.requestedBy?.username ?? 'Planista';
  return `${who} prosi o przekazanie do Trade zlecenia obsadzonego pojazdem${vehicle} — pojazd zejdzie ze zlecenia.`;
}

/** Przycisk akceptacji i komunikat po niej — po rodzaju wniosku. */
export function approveLabels(kind: string): { button: string; done: string; withdrawn: string } {
  if (kind === REQUEST_KIND.TRADE_TRANSFER) {
    return {
      button: 'Przekaż do Trade',
      done: 'Zlecenie przekazane do Trade.',
      withdrawn: 'Wniosek o przekazanie do Trade wycofany.',
    };
  }
  if (kind === REQUEST_KIND.VEHICLE_CHANGE) {
    return {
      button: 'Zmień pojazd',
      done: 'Pojazd zmieniony zgodnie z wnioskiem.',
      withdrawn: 'Wniosek o zmianę pojazdu wycofany.',
    };
  }
  return {
    button: 'Odepnij pojazd',
    done: 'Pojazd odpięty. Zlecenie wróciło do planowania.',
    withdrawn: 'Wniosek o odpięcie wycofany.',
  };
}
